import { useMemo, useRef, useState, type ReactNode } from "react";
import { ArrowLeft, ChevronRight, Folder } from "lucide-react";
import {
  Button,
  DataTable,
  FeedbackState,
  localTablePage,
  type DataTableColumn,
  type DataTableState,
} from "@h2j-data-team/ui";

import { formatBytes } from "@/lib/health-format";
import { tableMessages, tableSearch } from "@/lib/ui-labels";
import { useSearchHotkey } from "@/lib/use-search-hotkey";
import { useTableDensity } from "@/lib/use-table-density";
import { ListError, ListLoading, RowButton } from "./qvd-list-parts";
import { ALL_ROWS, noop, STATIC_TABLE_STATE } from "./qvd-list-utils";

/*
 * The space → folder drill-down shared by the storage analysis ("Size by space")
 * and the capacity ("By file → By space"): one table grammar for the list of
 * spaces and for every folder level, so drilling in does not change the shape of
 * the table under the cursor. The texts come from the caller, which owns the
 * i18n namespace.
 */

export type UsageRow = {
  /** Unique within the table. */
  key: string;
  name: string;
  /** `space` and `dir` open a level below; `file` is inert. */
  kind: "space" | "dir" | "file";
  fileCount: number;
  bytes: number;
};

export type UsageHeaders = { name: string; files: string; size: string; pct: string };

const pct = (bytes: number, total: number) => (total > 0 ? Math.round((bytes / total) * 100) : 0);

const DEFAULT_SORT = { id: "bytes", direction: "desc" } as const;

/**
 * Rows of spaces or folder entries with file count, size and share of `total`.
 * With `searchPlaceholder` the heads sort (by name, files, size, share) and a
 * search box filters by name, both in memory.
 */
export function UsageTable({
  label,
  rows,
  total,
  headers,
  emptyTitle,
  openLabel,
  onOpen,
  searchPlaceholder,
  footer,
}: {
  label: string;
  rows: UsageRow[];
  /** The denominator of the share column. */
  total: number;
  headers: UsageHeaders;
  emptyTitle: string;
  openLabel: (name: string) => string;
  onOpen: (row: UsageRow) => void;
  searchPlaceholder?: string;
  footer?: ReactNode;
}) {
  const { density } = useTableDensity();
  const searchRef = useRef<HTMLInputElement>(null);
  useSearchHotkey(searchRef);
  const interactive = searchPlaceholder !== undefined;
  const [state, setState] = useState<DataTableState>({
    ...STATIC_TABLE_STATE,
    sort: interactive ? DEFAULT_SORT : undefined,
  });

  const columns = useMemo<DataTableColumn<UsageRow>[]>(
    () => [
      {
        id: "name",
        header: headers.name,
        sort: interactive ? "text" : undefined,
        value: (r) => r.name,
        rowLink: true,
        cell: (r) =>
          r.kind === "file" ? (
            <span className="block truncate pl-5 font-mono" title={r.name}>
              {r.name}
            </span>
          ) : (
            <RowButton label={openLabel(r.name)} onClick={() => onOpen(r)}>
              <span className="flex min-w-0 items-center gap-1.5">
                {r.kind === "dir" && (
                  <Folder aria-hidden className="size-3.5 shrink-0 text-faint" />
                )}
                <span
                  className={r.kind === "dir" ? "truncate font-mono font-semibold" : "truncate"}
                >
                  {r.name}
                </span>
                <ChevronRight aria-hidden className="size-3.5 shrink-0 text-faint" />
              </span>
            </RowButton>
          ),
      },
      {
        id: "files",
        header: headers.files,
        width: 120,
        align: "end",
        variant: "numeric",
        sort: interactive ? "number" : undefined,
        value: (r) => r.fileCount,
        cell: (r) => <span className="text-dim">{r.fileCount.toLocaleString()}</span>,
      },
      {
        id: "bytes",
        header: headers.size,
        width: 130,
        align: "end",
        variant: "numeric",
        sort: interactive ? "number" : undefined,
        value: (r) => r.bytes,
        cell: (r) => formatBytes(r.bytes),
      },
      {
        id: "pct",
        header: headers.pct,
        width: 130,
        align: "end",
        variant: "numeric",
        sort: interactive ? "number" : undefined,
        value: (r) => r.bytes,
        cell: (r) => <span className="text-dim">{pct(r.bytes, total)}%</span>,
      },
    ],
    [headers, interactive, openLabel, onOpen, total],
  );

  const shown = useMemo(
    () =>
      interactive ? localTablePage(rows, { ...state, pageSize: ALL_ROWS }, { columns }).rows : rows,
    [interactive, rows, state, columns],
  );

  return (
    <DataTable
      label={label}
      density={density}
      columns={columns}
      rows={shown}
      getRowId={(r) => r.key}
      state={state}
      onStateChange={interactive ? setState : noop}
      footer={footer}
      search={
        interactive
          ? {
              ...tableSearch(searchPlaceholder),
              inputRef: searchRef,
              shortcutHint: "/",
            }
          : undefined
      }
      messages={tableMessages({ empty: { title: emptyTitle } })}
    />
  );
}

export type DirectoryTexts = {
  /** Name of the folder table. */
  label: string;
  /** Name of the breadcrumb. */
  path: string;
  backToSpaces: string;
  up: string;
  openDir: (name: string) => string;
  error: string;
  emptyDir: string;
  /** "12 files · 3 GB in this folder". */
  summary: (data: DirectoryData) => string;
  /** The "showing only the N largest" note, when the level was cut. */
  truncated: (count: number) => string;
  headers: UsageHeaders;
};

export type DirectoryData = {
  children: { kind: "dir" | "file"; name: string; fileCount: number; bytes: number }[];
  totalFiles: number;
  totalBytes: number;
  truncated: boolean;
};

/**
 * One level of a space's directory tree. Children are aggregated server-side —
 * a space can hold hundreds of thousands of files — so each level is one fetch
 * and the % share is measured against THIS level's total, not the space's.
 */
export function DirectoryLevel({
  spaceName,
  prefix,
  onPrefix,
  onExit,
  query,
  texts,
}: {
  spaceName: string;
  prefix: string;
  onPrefix: (prefix: string) => void;
  onExit: () => void;
  query: {
    data: DirectoryData | undefined;
    isLoading: boolean;
    isError: boolean;
    error: unknown;
    refetch: () => unknown;
  };
  texts: DirectoryTexts;
}) {
  const { data, isLoading, isError, error, refetch } = query;

  // "Vendas/2026/" → ["Vendas", "2026"]; the prefix of segment i is the first
  // i + 1 of them, slash-terminated.
  const segments = prefix ? prefix.slice(0, -1).split("/") : [];
  const prefixOf = (upTo: number) =>
    segments
      .slice(0, upTo)
      .map((s) => `${s}/`)
      .join("");
  const goUp = () => (segments.length === 0 ? onExit() : onPrefix(prefixOf(segments.length - 1)));

  const rows = useMemo<UsageRow[]>(
    () =>
      (data?.children ?? []).map((c) => ({
        key: `${c.kind}:${c.name}`,
        name: c.name,
        kind: c.kind,
        fileCount: c.fileCount,
        bytes: c.bytes,
      })),
    [data],
  );

  return (
    <div className="flex flex-col">
      <nav aria-label={texts.path} className="pb-3">
        <ol className="flex min-w-0 flex-wrap items-center gap-1">
          <li>
            <Button
              variant="ghost"
              size="icon-compact"
              aria-label={segments.length === 0 ? texts.backToSpaces : texts.up}
              onClick={goUp}
            >
              <ArrowLeft />
            </Button>
          </li>
          <li className="min-w-0">
            {segments.length === 0 ? (
              <span aria-current="location" className="block max-w-56 truncate font-semibold">
                {spaceName}
              </span>
            ) : (
              <Button
                variant="ghost"
                size="compact"
                className="max-w-56 truncate"
                onClick={() => onPrefix("")}
              >
                {spaceName}
              </Button>
            )}
          </li>
          {segments.map((segment, i) => (
            <li key={prefixOf(i + 1)} className="flex min-w-0 items-center gap-1">
              <span aria-hidden className="text-faint">
                /
              </span>
              {i === segments.length - 1 ? (
                <span
                  aria-current="location"
                  className="block max-w-56 truncate font-mono font-semibold"
                >
                  {segment}
                </span>
              ) : (
                <Button
                  variant="ghost"
                  size="compact"
                  className="max-w-56 truncate font-mono"
                  onClick={() => onPrefix(prefixOf(i + 1))}
                >
                  {segment}
                </Button>
              )}
            </li>
          ))}
        </ol>
      </nav>

      {isLoading ? (
        <ListLoading />
      ) : isError ? (
        <ListError error={error} title={texts.error} onRetry={() => void refetch()} />
      ) : !data || data.children.length === 0 ? (
        <FeedbackState state="empty" icon={<Folder />} title={texts.emptyDir} />
      ) : (
        <>
          <p className="pb-2 text-sm text-dim">{texts.summary(data)}</p>
          <UsageTable
            label={texts.label}
            rows={rows}
            total={data.totalBytes}
            headers={texts.headers}
            emptyTitle={texts.emptyDir}
            openLabel={texts.openDir}
            onOpen={(row) => onPrefix(`${prefix}${row.name}/`)}
            footer={
              data.truncated ? (
                <p className="text-xs text-faint">{texts.truncated(data.children.length)}</p>
              ) : undefined
            }
          />
        </>
      )}
    </div>
  );
}
